// apps/provider/src/services/api/broadcasts.js
// Domain module for email broadcast operations
//
// Methods:
//   CRUD: getEmailBroadcasts, getEmailBroadcast, createEmailBroadcast, updateEmailBroadcast, deleteEmailBroadcast
//   Actions: previewBroadcastRecipients, sendEmailBroadcast
//   Monitoring: getEmailBroadcastStatus
//
// Response types: @prism-ehr/shared → EmailBroadcast, EmailBroadcastWithCreator,
//   BroadcastRecipientPreview, BroadcastStatusResponse

/**
 * Creates email broadcast API methods.
 * Bound to ApiClient instance via Object.assign in index.js.
 *
 * @returns {Object} Broadcast methods
 */
export function createBroadcastsMethods() {
  return {
    /**
     * List email broadcasts for the current organization.
     *
     * Xano: GET /email-broadcasts
     *
     * @param {Object} [filters]
     * @param {string} [filters.status] - Filter by broadcast status
     * @param {number} [filters.page=1]
     * @param {number} [filters.per_page=25]
     * @returns {Promise<{items: import('@prism-ehr/shared').EmailBroadcastWithCreator[], curPage: number, perPage: number, itemsTotal: number, pageTotal: number}>}
     */
    getEmailBroadcasts(filters = {}) {
      const params = new URLSearchParams();
      if (filters.status) params.set('status', filters.status);
      if (filters.page) params.set('page', String(filters.page));
      if (filters.per_page) params.set('per_page', String(filters.per_page));
      const qs = params.toString();
      return this.request(`/email-broadcasts${qs ? '?' + qs : ''}`, { method: 'GET' });
    },

    /**
     * Get a single email broadcast.
     *
     * Xano: GET /email-broadcasts/{email_broadcast_id}
     *
     * @param {string} broadcastId
     * @returns {Promise<import('@prism-ehr/shared').EmailBroadcastWithCreator>}
     */
    getEmailBroadcast(broadcastId) {
      return this.request(`/email-broadcasts/${broadcastId}`, { method: 'GET' });
    },

    /**
     * Create a new email broadcast draft.
     *
     * Xano: POST /email-broadcasts
     *
     * @param {Object} data
     * @param {string} data.name - Internal label
     * @param {string} data.subject - Email subject line
     * @param {string} data.body_html - TipTap HTML body
     * @param {Object} [data.filters] - Recipient filters
     * @returns {Promise<import('@prism-ehr/shared').EmailBroadcast>}
     */
    createEmailBroadcast(data) {
      return this.request('/email-broadcasts', {
        method: 'POST',
        body: JSON.stringify(data),
      });
    },

    /**
     * Update an email broadcast draft.
     *
     * Xano: PATCH /email-broadcasts/{email_broadcast_id}
     *
     * @param {string} broadcastId
     * @param {Object} data - Fields to update
     * @returns {Promise<import('@prism-ehr/shared').EmailBroadcast>}
     */
    updateEmailBroadcast(broadcastId, data) {
      return this.request(`/email-broadcasts/${broadcastId}`, {
        method: 'PATCH',
        body: JSON.stringify(data),
      });
    },

    /**
     * Delete an email broadcast draft.
     *
     * Xano: DELETE /email-broadcasts/{email_broadcast_id}
     *
     * @param {string} broadcastId
     * @returns {Promise<void>}
     */
    deleteEmailBroadcast(broadcastId) {
      return this.request(`/email-broadcasts/${broadcastId}`, { method: 'DELETE' });
    },

    /**
     * Preview recipient counts for given filters without creating records.
     *
     * Xano: POST /email-broadcasts-preview-recipients
     *
     * @param {Object} filters
     * @param {string} [filters.case_status] - active, discharged, on_hold, cancelled
     * @param {string} [filters.provider_id]
     * @param {string[]} [filters.location_ids]
     * @returns {Promise<import('@prism-ehr/shared').BroadcastRecipientPreview>}
     */
    previewBroadcastRecipients(filters) {
      return this.request('/email-broadcasts-preview-recipients', {
        method: 'POST',
        body: JSON.stringify(filters),
      });
    },

    /**
     * Send an email broadcast (triggers fan-out to notification queue).
     *
     * Xano: POST /email-broadcasts-send
     *
     * @param {string} broadcastId
     * @returns {Promise<import('@prism-ehr/shared').EmailBroadcast>}
     */
    sendEmailBroadcast(broadcastId) {
      return this.request('/email-broadcasts-send', {
        method: 'POST',
        body: JSON.stringify({ broadcast_id: broadcastId }),
      });
    },

    /**
     * Get real-time send status/counts for a broadcast.
     *
     * Xano: GET /email-broadcasts-status
     *
     * @param {string} broadcastId
     * @returns {Promise<import('@prism-ehr/shared').BroadcastStatusResponse>}
     */
    getEmailBroadcastStatus(broadcastId) {
      return this.request(`/email-broadcasts-status?broadcast_id=${broadcastId}`, { method: 'GET' });
    },
  };
}
